"use client";

import type React from "react";
import { useState, useEffect, useCallback } from "react";
import {
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  VStack,
  useToast,
  Flex,
  Spinner,
} from "@chakra-ui/react";
import { BackgroundSoundSelect } from "./BackgroundSoundSelect";
import { ResponsivenessSlider } from "./ResponsivenessSlider";
import { InterruptionSensitivitySlider } from "./InterruptionSensitivitySlider";
import { BackchannelingToggle } from "./BackchannelingToggle";
import { BoostedKeywords } from "./BoostedKeywords";
import { SpeechNormalizationToggle } from "./SpeechNormalizationToggle";
import { ReminderMessageSettings } from "./ReminderMessageSettings";
import {
  PronunciationDictionary,
  type PronunciationEntry,
} from "./PronunciationDictionary";
import { agentService } from "../../../voice-services/agentService";

interface SpeechSettingsProps {
  agentId: string;
}

export interface SpeechSettingsData {
  ambient_sound?:
    | "coffee-shop"
    | "convention-hall"
    | "summer-outdoor"
    | "mountain-outdoor"
    | "static-noise"
    | "call-center"
    | null;
  responsiveness?: number;
  interruption_sensitivity?: number;
  enable_backchannel?: boolean;
  backchannel_frequency?: number;
  backchannel_words?: string[] | null;
  boosted_keywords?: string[] | null;
  normalize_for_speech?: boolean;
  reminder_trigger_ms?: number;
  reminder_max_count?: number;
  pronunciation_dictionary?: PronunciationEntry[] | null;
}

export const SpeechSettings: React.FC<SpeechSettingsProps> = ({ agentId }) => {
  const [settings, setSettings] = useState<SpeechSettingsData>(
    {} as SpeechSettingsData,
  );
  const [isLoading, setIsLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    const fetchAgentSettings = async () => {
      setIsLoading(true);
      try {
        const data = await agentService.getAgentDetails(agentId);
        setSettings({
          ambient_sound: data.ambient_sound ?? null,
          responsiveness:
            data.responsiveness !== undefined ? data.responsiveness : 1,
          interruption_sensitivity:
            data.interruption_sensitivity !== undefined
              ? data.interruption_sensitivity
              : 1,
          enable_backchannel: data.enable_backchannel || false,
          backchannel_frequency:
            data.backchannel_frequency !== undefined
              ? data.backchannel_frequency
              : 0.8,
          backchannel_words: data.backchannel_words || null,
          boosted_keywords: data.boosted_keywords || null,
          normalize_for_speech: data.normalize_for_speech || false,
          reminder_trigger_ms: data.reminder_trigger_ms || 10000,
          reminder_max_count:
            data.reminder_max_count !== undefined ? data.reminder_max_count : 1,
          pronunciation_dictionary: data.pronunciation_dictionary || null,
        });
      } catch (error) {
        console.error("Error fetching agent settings:", error);
        toast({
          title: "Error fetching speech settings",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchAgentSettings();
  }, [agentId, toast]);

  const updateAgentSettings = useCallback(
    async (newSettings: Partial<SpeechSettingsData>) => {
      try {
        await agentService.updateAgentSettings(agentId, newSettings as any);

        setSettings((prev) => ({ ...prev, ...newSettings }));
        toast({
          title: "Speech settings updated",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
        return true;
      } catch (error: any) {
        console.error("Error updating speech settings:", error);
        toast({
          title: "Error updating speech settings",
          description:
            error?.response?.data?.message || "An unexpected error occurred",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return false;
      }
    },
    [agentId, toast],
  );

  if (isLoading) {
    return (
      <Flex h="100%" w="100%" justify="center" align="center">
        <Spinner size="md" color="primary.500" />
      </Flex>
    );
  }

  return (
    <AccordionItem border="none">
      <h2>
        <AccordionButton py={3} px={4} _hover={{ bg: "primary.50" }}>
          <Box
            flex="1"
            textAlign="left"
            fontSize="sm"
            display="flex"
            alignItems="center"
            gap={2}
            color="primary.600"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M13.0836 8.10309C12.7663 5.64726 10.6672 3.75 8.125 3.75C5.36357 3.75 3.125 5.98857 3.125 8.75C3.125 9.93275 3.53567 11.0196 4.22219 11.8757C4.72719 12.5054 5.00021 13.2033 5.00016 13.9413L5 16.25H10.625L10.6256 14.375H11.875C12.5654 14.375 13.125 13.8154 13.125 13.125V11.2944L14.3496 10.7698C14.5641 10.6779 14.5828 10.4604 14.4901 10.3148L13.0836 8.10309ZM4.375 8.75C4.375 6.67893 6.05393 5 8.125 5C10.0152 5 11.6041 6.40713 11.8438 8.26324L11.8795 8.53914L12.8436 10.0551L11.875 10.4699V13.125H9.37606L9.37544 15H6.25008L6.25016 13.9414C6.25022 12.917 5.88103 11.9462 5.19736 11.0937C4.66761 10.4331 4.375 9.61556 4.375 8.75ZM15.7209 13.814L14.6808 13.1206C15.2773 12.2276 15.625 11.1544 15.625 10.0001C15.625 8.84562 15.2773 7.77244 14.6808 6.87951L15.7209 6.18607C16.45 7.27741 16.875 8.58909 16.875 10.0001C16.875 11.411 16.45 12.7226 15.7209 13.814Z"
                fill="currentColor"
              />
            </svg>
            Speech Settings
          </Box>
          <AccordionIcon color="primary.600" />
        </AccordionButton>
      </h2>

      <AccordionPanel
        pb={4}
        bg="primary.50"
        borderX="1px solid"
        borderBottom="1px solid"
        borderColor="gray.200"
        borderBottomRadius="md"
      >
        <VStack spacing={6} align="stretch">
          <BackgroundSoundSelect
            value={settings.ambient_sound ?? null}
            onChange={(value) =>
              updateAgentSettings({
                ambient_sound:
                  value === "coffee-shop" ||
                  value === "convention-hall" ||
                  value === "summer-outdoor" ||
                  value === "mountain-outdoor" ||
                  value === "static-noise" ||
                  value === "call-center" ||
                  value === null
                    ? value
                    : null,
              })
            }
          />

          <ResponsivenessSlider
            value={settings.responsiveness}
            onChange={(value) => updateAgentSettings({ responsiveness: value })}
          />

          <InterruptionSensitivitySlider
            value={settings.interruption_sensitivity}
            onChange={(value) =>
              updateAgentSettings({ interruption_sensitivity: value })
            }
          />

          <BackchannelingToggle
            value={settings.enable_backchannel}
            frequency={settings.backchannel_frequency}
            words={settings.backchannel_words}
            onChange={(value) =>
              updateAgentSettings({ enable_backchannel: value })
            }
            onFrequencyChange={(value) =>
              updateAgentSettings({ backchannel_frequency: value })
            }
            onWordsChange={(value) =>
              updateAgentSettings({ backchannel_words: value })
            }
          />

          <BoostedKeywords
            value={settings.boosted_keywords}
            onChange={(value) =>
              updateAgentSettings({ boosted_keywords: value })
            }
          />

          <SpeechNormalizationToggle
            value={settings.normalize_for_speech}
            onChange={(value) =>
              updateAgentSettings({ normalize_for_speech: value })
            }
          />

          <ReminderMessageSettings
            triggerMs={settings.reminder_trigger_ms}
            maxCount={settings.reminder_max_count}
            onChange={(triggerMs, maxCount) =>
              updateAgentSettings({
                reminder_trigger_ms: triggerMs,
                reminder_max_count: maxCount,
              })
            }
          />

          <PronunciationDictionary
            value={settings.pronunciation_dictionary}
            onChange={(value) =>
              updateAgentSettings({ pronunciation_dictionary: value })
            }
          />
        </VStack>
      </AccordionPanel>
    </AccordionItem>
  );
};
